/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/authenticationRuleApi.ts
 🗓️ Created: 2025-01-28
 🧩 Description:
    API functions for Authentication Rules.
    Centralizes all API calls related to authentication rules and codes.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  AuthenticationRule,
  AuthenticationRuleWithCodes,
  ListAuthenticationRulesResponse,
  AuthenticationRulePayload,
  AuthenticationCodePayload,
  ImportCodesPayload,
  ImportCodesResult,
  AuthenticationCode,
} from '../types';

/**
 * Fetch Authentication Rules with pagination and search
 */
export async function fetchAuthenticationRules(params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  domainId?: number;
}): Promise<ListAuthenticationRulesResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search && { search: params.search }),
    ...(params.domainId != null && { domainId: params.domainId.toString() }),
  });

  const url = `${buildApiUrl('/api/telephony/authentication-rules')}?${queryParams}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch Authentication Rules');
  }

  return result;
}

/**
 * Fetch single Authentication Rule by ID (with codes)
 */
export async function fetchAuthenticationRule(
  id: string | number,
): Promise<AuthenticationRuleWithCodes> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/authentication-rules/${id}`),
    {
      method: 'GET',
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch Authentication Rule');
  }

  return result.data;
}

/**
 * Create new Authentication Rule
 */
export async function createAuthenticationRule(
  payload: AuthenticationRulePayload,
): Promise<AuthenticationRuleWithCodes> {
  const response = await fetch(
    buildApiUrl('/api/telephony/authentication-rules'),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || 'Failed to create Authentication Rule');
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to create Authentication Rule');
  }

  return result.data;
}

/**
 * Update Authentication Rule
 */
export async function updateAuthenticationRule(
  id: string | number,
  payload: AuthenticationRulePayload,
): Promise<AuthenticationRuleWithCodes> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/authentication-rules/${id}`),
    {
      method: 'PUT',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || 'Failed to update Authentication Rule');
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to update Authentication Rule');
  }

  return result.data;
}

/**
 * Delete Authentication Rule
 */
export async function deleteAuthenticationRule(id: string | number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/authentication-rules/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    },
  );

  const result = await response.json().catch(() => null);

  if (!response.ok || !result?.success) {
    const message = result?.message || 'Failed to delete Authentication Rule';
    throw new Error(message);
  }
}

/**
 * Create Authentication Code
 */
export async function createAuthenticationCode(
  ruleId: string | number,
  payload: AuthenticationCodePayload,
): Promise<AuthenticationCode> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/authentication-rules/${ruleId}/codes`),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || 'Failed to create Authentication Code');
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to create Authentication Code');
  }

  return result.data;
}

/**
 * Update Authentication Code
 */
export async function updateAuthenticationCode(
  ruleId: string | number,
  codeId: string | number,
  payload: AuthenticationCodePayload,
): Promise<AuthenticationCode> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/authentication-rules/${ruleId}/codes/${codeId}`),
    {
      method: 'PUT',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || 'Failed to update Authentication Code');
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to update Authentication Code');
  }

  return result.data;
}

/**
 * Delete Authentication Code
 */
export async function deleteAuthenticationCode(
  ruleId: string | number,
  codeId: string | number,
): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/authentication-rules/${ruleId}/codes/${codeId}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    },
  );

  const result = await response.json().catch(() => null);

  if (!response.ok || !result?.success) {
    const message = result?.message || 'Failed to delete Authentication Code';
    throw new Error(message);
  }
}

/**
 * Import Authentication Codes from CSV
 */
export async function importAuthenticationCodes(
  ruleId: string | number,
  payload: ImportCodesPayload,
): Promise<ImportCodesResult> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/authentication-rules/${ruleId}/codes/import`),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || 'Failed to import Authentication Codes');
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to import Authentication Codes');
  }

  return result.data;
}

